import React, { useEffect, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  Campaign,
  CampaignItem,
  CampaignItemPipelineStage,
  Disparo,
  KanbanColumnLabels,
  Lead,
  Usuario,
  WhatsAppAccount,
} from '../../types';
import { KanbanColumn } from '../KanbanColumn/KanbanColumn';
import { LeadCard } from '../LeadCard/LeadCard';
import { KanbanFilters, KanbanFiltersValue } from '../KanbanFilters/KanbanFilters';
import { KanbanIaToggle } from './KanbanIaToggle';
import { AutorunAdminMenu } from './AutorunAdminMenu';
import { LeadDetails } from '../../pages/LeadDetails/LeadDetails';
import { useLeads } from '../../context/LeadsContext';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import { getErrorMessage } from '../../types';
import { wsService } from '../../services/websocket';
import { CountdownTimer } from '../CountdownTimer/CountdownTimer';
import { CampaignItemsListModal } from '../CampaignItemsListModal/CampaignItemsListModal';
import { CampaignExecutionCard } from '../CampaignExecutionCard/CampaignExecutionCard';
import { CampaignExecutionDrawer } from '../CampaignExecutionDrawer/CampaignExecutionDrawer';
import { formatWhatsappSelectLabel } from '../../utils/whatsappAccountLabels';
import { buildCreateDisparoBodyFromCampaignDefaults } from '../../utils/campaignDisparoQuickDefaults';
import { CircleCheck, Pause, Play } from 'lucide-react';
import './KanbanBoard.css';

const CAMPAIGNS_ENABLED = process.env.REACT_APP_CAMPAIGNS !== 'false';

const KANBAN_STORAGE_KEY = 'ghicrm-kanban-state';

function readSession<T>(key: string, fallback: T): T {
  try {
    const raw = sessionStorage.getItem(KANBAN_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed[key] ?? fallback;
  } catch {
    return fallback;
  }
}

type PosVendaStatus = NonNullable<Lead['posVendaStatus']>;
type KanbanTab = 'vendas' | 'pos_vendas' | 'campanhas';
type ActiveBoardView = KanbanTab | 'lead';
type FinalizadoPeriod = 'today' | 'week' | 'month' | 'year';

type PipelineSummary = {
  leads: Lead[];
  unreadCount: number;
  overdueCount: number;
};

type DropTarget =
  | { type: 'vendas'; status: Lead['status']; atendenteId?: string }
  | { type: 'pos_vendas'; posVendaStatus: PosVendaStatus; atendenteId?: string };

const FINALIZADO_PERIOD_LABELS: Record<FinalizadoPeriod, string> = {
  today: 'Hoje',
  week: 'Semana',
  month: 'Mes',
  year: 'Ano',
};


const CAMPAIGN_TERMINAL_STAGES: CampaignItemPipelineStage[] = ['sucesso', 'perdido', 'encerrado'];

/** Disparo com fila “ativa”: cartão agregado fica na coluna Disparos em execução, não em Pendente. */
const DISPARO_CARD_IN_EXEC_COLUMN: Disparo['status'][] = [
  'em_execucao',
  'pausado',
  'pausado_horario',
  'max_atingido',
];

function sortCampaignIdEntries(
  entries: Array<[string, CampaignItem[]]>,
  campaignById: Map<string, Campaign>
): Array<[string, CampaignItem[]]> {
  return entries.slice().sort((a, b) => {
    const ad = campaignById.get(a[0])?.updatedAt || campaignById.get(a[0])?.createdAt || '';
    const bd = campaignById.get(b[0])?.updatedAt || campaignById.get(b[0])?.createdAt || '';
    return new Date(bd).getTime() - new Date(ad).getTime();
  });
}

export type KanbanBoardVariant = 'default' | 'campaignsExecution';

interface KanbanBoardProps {
  variant?: KanbanBoardVariant;
  onCreateLead?: () => void;
  isCreateLeadExpanded?: boolean;
  createLeadPanel?: React.ReactNode;
  createDisparoPanel?: React.ReactNode;
  initialLeadId?: string | null;
  onInitialLeadConsumed?: () => void;
  refreshCampaignsRef?: React.MutableRefObject<(() => void) | null>;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({
  variant = 'default',
  onCreateLead,
  isCreateLeadExpanded = false,
  createLeadPanel,
  createDisparoPanel,
  initialLeadId,
  onInitialLeadConsumed,
  refreshCampaignsRef,
}) => {
  const isCampaignsExecution = variant === 'campaignsExecution';
  const {
    leads,
    updateLeadStatus,
    updateLeadAtendente,
    convertLead,
    loseLead,
    loading,
    startLeadIa,
    assumeHumanLeadIa,
    stopLeadIa,
  } = useLeads();
  const { isAuthenticated, isAdmin, isGerente, organizationId } = useAuth();
  const [orgAiEnabled, setOrgAiEnabled] = useState(false);
  const [iaOrchestrationPendingLeadId, setIaOrchestrationPendingLeadId] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setOrgAiEnabled(false);
      return;
    }
    apiService
      .getAISettings(organizationId)
      .then((s) => setOrgAiEnabled(Boolean(s?.enabled)))
      .catch(() => setOrgAiEnabled(false));
  }, [organizationId]);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState<KanbanTab>(
    () => readSession<KanbanTab>('activeTab', 'vendas')
  );
  const [activeView, setActiveView] = React.useState<ActiveBoardView>(
    () => readSession<KanbanTab>('activeTab', 'vendas') as ActiveBoardView
  );
  const [selectedLeadId, setSelectedLeadId] = React.useState<string | null>(null);
  const [atendentes, setAtendentes] = useState<Usuario[]>([]);
  const [filters, setFilters] = useState<KanbanFiltersValue>(
    () =>
      readSession<KanbanFiltersValue>('filters', {
        nome: '',
        cpf: '',
        telefone: '',
        atendenteId: '',
        somenteHandoffFunilIa: false,
        somenteReativacaoIa: false,
      })
  );
  const [finalizadoPeriod, setFinalizadoPeriod] = useState<FinalizadoPeriod>(
    () => readSession<FinalizadoPeriod>('finalizadoPeriod', 'today')
  );
  const [campaignFinalizadoPeriod, setCampaignFinalizadoPeriod] = useState<FinalizadoPeriod>(() =>
    readSession<FinalizadoPeriod>(
      'campaignFinalizadoPeriod',
      readSession<FinalizadoPeriod>('campaignPerdidoPeriod', 'today')
    )
  );
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignItems, setCampaignItems] = useState<CampaignItem[]>([]);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [draggingCampaignItemId, setDraggingCampaignItemId] = useState<string | null>(null);
  /** Modo hub Execução: modal com lista filtrável de itens da campanha (fora do Kanban). */
  const [campaignItemsModalCampaignId, setCampaignItemsModalCampaignId] = useState<string | null>(null);
  const [executionDrawerCampaignId, setExecutionDrawerCampaignId] = useState<string | null>(null);
  const [waAccounts, setWaAccounts] = useState<WhatsAppAccount[]>([]);
  const [usuariosOrg, setUsuariosOrg] = useState<Usuario[]>([]);
  const [orgDefaultRemarketingMeses, setOrgDefaultRemarketingMeses] = useState<number | null | undefined>(undefined);
  const [disparosByCampaign, setDisparosByCampaign] = useState<Map<string, Disparo>>(new Map());
  /** Map campaignId → soma de `disparosRealizados` em TODOS os Disparos
   * (ativos ou concluídos). Usado pra:
   *   - Classificar campanha no Kanban sem items carregados (realizados > 0
   *     ⇒ items já foram pra stage 'disparado', mesmo com disparo concluído)
   *   - Calcular `remaining = campaignItemCount - realizados` pra decidir
   *     se Play ainda faz sentido (sobra fila) ou só Finalizar (tudo enviado). */
  const [realizadosByCampaign, setRealizadosByCampaign] = useState<Map<string, number>>(new Map());
  const [finalizingCampaignId, setFinalizingCampaignId] = useState<string | null>(null);
  const [quickStartingCampaignId, setQuickStartingCampaignId] = useState<string | null>(null);
  const [kanbanColumnLabels, setKanbanColumnLabels] = useState<KanbanColumnLabels>({});

  const canFilterByAtendente = isAdmin || isGerente;

  // Carregar lista de atendentes para permitir reatribuicao por qualquer usuario autenticado
  useEffect(() => {
    if (isAuthenticated) {
      apiService.getAtendentes().then(setAtendentes).catch(console.error);
    } else {
      setAtendentes([]);
    }
  }, [isAuthenticated]);

  // Carregar nomes customizados das colunas
  useEffect(() => {
    if (isAuthenticated && organizationId) {
      apiService.getKanbanColumnLabels(organizationId).then(setKanbanColumnLabels).catch(console.error);
    }
  }, [isAuthenticated, organizationId]);

  // Resolve column titles: custom label takes precedence over default
  const resolvedVendasColumns: { id: Lead['status']; title: string }[] = [
    { id: 'aguardando_contato', title: kanbanColumnLabels.vendas?.aguardandoContato || 'Aguardando Contato' },
    { id: 'em_atendimento', title: kanbanColumnLabels.vendas?.emAtendimento || 'Em Atendimento' },
    { id: 'em_negociacao', title: kanbanColumnLabels.vendas?.emNegociacao || 'Em Negociacao' },
    {
      id: 'urgente',
      title:
        kanbanColumnLabels.vendas?.urgentes ||
        kanbanColumnLabels.vendas?.inativos ||
        'Urgente',
    },
    { id: 'proposta', title: kanbanColumnLabels.vendas?.proposta || 'Proposta' },
    { id: 'fechamento', title: kanbanColumnLabels.vendas?.fechamento || 'Fechamento' },
  ];

  const resolvedPosVendaColumns: { id: PosVendaStatus; title: string }[] = [
    { id: 'conversoes_ganhas', title: kanbanColumnLabels.posVendas?.conversoesGanhas || 'Conversoes Ganhas' },
    { id: 'em_atendimento', title: kanbanColumnLabels.posVendas?.emAtendimento || 'Em Atendimento' },
    { id: 'aguardando_retorno', title: kanbanColumnLabels.posVendas?.aguardandoRetorno || 'Aguardando Retorno' },
    { id: 'finalizado', title: kanbanColumnLabels.posVendas?.finalizado || 'Finalizado' },
  ];

  const resolvedCampanhasLabels = {
    pendente: kanbanColumnLabels.campanhas?.pendente || 'Pendente',
    emExecucao: kanbanColumnLabels.campanhas?.emExecucao || 'Em execução',
    finalizado: kanbanColumnLabels.campanhas?.finalizado || 'Finalizado',
  };
  const resolvedCampaignActiveColumns: { id: 'pendente' | 'disparado'; title: string }[] = [
    { id: 'pendente', title: resolvedCampanhasLabels.pendente },
    { id: 'disparado', title: resolvedCampanhasLabels.emExecucao },
  ];

  useEffect(() => {
    if (!canFilterByAtendente && filters.atendenteId) {
      setFilters((prev) => ({ ...prev, atendenteId: '' }));
    }
  }, [canFilterByAtendente, filters.atendenteId]);

  useEffect(() => {
    if (!isCampaignsExecution) return;
    setActiveTab('campanhas');
    setActiveView('campanhas');
    setCampaignItemsModalCampaignId(null);
    setExecutionDrawerCampaignId(null);
  }, [isCampaignsExecution]);

  const loadCampaignKanbanMeta = isCampaignsExecution || (CAMPAIGNS_ENABLED && activeTab === 'campanhas');

  useEffect(() => {
    if (!loadCampaignKanbanMeta || !organizationId) return;
    let cancel = false;
    void (async () => {
      try {
        const [acc, users, org] = await Promise.all([
          apiService.getWhatsAppAccounts(),
          apiService.getUsuarios(),
          apiService.getOrganization(organizationId),
        ]);
        if (cancel) return;
        setWaAccounts(acc);
        setUsuariosOrg(users);
        setOrgDefaultRemarketingMeses(org.defaultRemarketingMeses);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [loadCampaignKanbanMeta, organizationId]);

  useEffect(() => {
    sessionStorage.setItem(KANBAN_STORAGE_KEY, JSON.stringify({
      filters,
      activeTab,
      finalizadoPeriod,
      campaignFinalizadoPeriod,
    }));
  }, [filters, activeTab, finalizadoPeriod, campaignFinalizadoPeriod]);

  useEffect(() => {
    if (!selectedLeadId) return;
    const exists = leads.some((lead) => lead.leadId === selectedLeadId);
    if (!exists) {
      setSelectedLeadId(null);
      if (activeView === 'lead') {
        setActiveView(activeTab);
      }
    }
  }, [selectedLeadId, leads, activeView, activeTab]);

  useEffect(() => {
    if (!initialLeadId) return;
    const lead = leads.find((l) => l.leadId === initialLeadId);
    if (!lead) return;

    const isPosVenda =
      lead.status === 'fechamento' && lead.statusFechamento === 'vendido';
    setActiveTab(isPosVenda ? 'pos_vendas' : 'vendas');
    setSelectedLeadId(initialLeadId);
    setActiveView('lead');
    onInitialLeadConsumed?.();
  }, [initialLeadId, leads, onInitialLeadConsumed]);

  const loadCampaignData = React.useCallback(async () => {
    let hasVisible = false;
    try {
      setCampaignLoading(true);
      // Campanhas. Filtra por permissão de dispatch (o kanban só mostra
      // `viewerCanDispatch === true`). UI já tem estrutura logo após.
      const cList = await apiService.listCampaigns();
      const visibleCampaigns = cList.filter((c) => c.viewerCanDispatch === true);
      setCampaigns(visibleCampaigns);
      hasVisible = visibleCampaigns.length > 0;
    } catch (error) {
      console.error('Erro ao carregar campanhas:', error);
    } finally {
      setCampaignLoading(false);
    }

    if (!hasVisible) {
      setCampaignItems([]);
      setDisparosByCampaign(new Map());
      setRealizadosByCampaign(new Map());
      return;
    }

    // Disparos (1 call). Extrai 2 sinais:
    //   1. `disparosByCampaign`: só os em estado ativo (alimenta progresso +
    //      `disparoCardInExecColumn`).
    //   2. `realizadosByCampaign`: soma de `disparosRealizados` em todos os
    //      Disparos (ativos OU concluídos). Suprime lazy load dos items pra
    //      classificação e pra decidir Play vs Finalizar (quando todos
    //      enviados, só Finalizar — Play seria redundante).
    apiService
      .getDisparos()
      .then((dList) => {
        const dMap = new Map<string, Disparo>();
        const realizadosMap = new Map<string, number>();
        for (const d of dList) {
          const realizados = d.disparosRealizados ?? 0;
          if (realizados > 0) {
            realizadosMap.set(
              d.campaignId,
              (realizadosMap.get(d.campaignId) ?? 0) + realizados
            );
          }
          if (['criado', 'em_execucao', 'pausado', 'pausado_horario', 'max_atingido'].includes(d.status)) {
            dMap.set(d.campaignId, d);
          }
        }
        setDisparosByCampaign(dMap);
        setRealizadosByCampaign(realizadosMap);
      })
      .catch((err) => {
        console.error('Erro ao carregar disparos:', err);
      });

    // Items NÃO são pré-carregados. Antes a tela disparava N (ou 1 com plural)
    // GET /campaign-items só pra alimentar progresso e badges agregados.
    // Conta AWS está em 10 concurrent Lambdas; um refresh do Kanban junto
    // com webhooks Evolution detonava throttle (vimos 31 503s/min em prod).
    // Agora: items entram lazy pelos handlers que abrem drawer/modal
    // (`useEffect` mais abaixo), só pra campanha clicada.
  }, []);

  useEffect(() => {
    void loadCampaignData();
  }, [loadCampaignData]);

  useEffect(() => {
    if (refreshCampaignsRef) {
      refreshCampaignsRef.current = loadCampaignData;
    }
  }, [refreshCampaignsRef, loadCampaignData]);

  useEffect(() => {
    const handleDisparoExecuted = (msg: any) => {
      if (msg.type === 'disparo_executed' && msg.data) {
        const {
          campaignItemId,
          campaignId,
          disparo: updatedDisparo,
          skippedUnsubscribed,
          skippedPosVendas,
          sendSuccess,
        } = msg.data;
        const nowIso = new Date().toISOString();
        setCampaignItems((prev) =>
          prev.map((item) =>
            item.campaignItemId === campaignItemId
              ? skippedUnsubscribed
                ? {
                    ...item,
                    pipelineStage: 'encerrado' as CampaignItemPipelineStage,
                    status: 'nao_disparado',
                    reason: 'optout',
                    disparoStatus: 'skipped',
                    skipReason: 'unsubscribed',
                    updatedAt: nowIso,
                  }
                : skippedPosVendas
                  ? {
                      ...item,
                      pipelineStage: 'encerrado' as CampaignItemPipelineStage,
                      status: 'nao_disparado',
                      reason: 'posvendas',
                      disparoStatus: 'skipped',
                      skipReason: 'pos_vendas_active',
                      updatedAt: nowIso,
                    }
                  : {
                      ...item,
                      pipelineStage: 'disparado' as CampaignItemPipelineStage,
                      status: 'disparado',
                      reason: undefined,
                      disparoStatus: sendSuccess ? 'sent' : 'failed',
                      disparadoAt: item.disparadoAt || nowIso,
                      updatedAt: nowIso,
                    }
              : item
          )
        );
        setDisparosByCampaign((prev) => {
          const next = new Map(prev);
          if (updatedDisparo) {
            next.set(campaignId, updatedDisparo);
          } else {
            const d = prev.get(campaignId);
            if (d) {
              next.set(campaignId, { ...d, disparosRealizados: d.disparosRealizados + 1 });
            }
          }
          return next;
        });
      }
    };
    const handleCampaignItemUpdated = (msg: any) => {
      if (msg.type === 'campaign_item_updated' && msg.data) {
        const updates = Array.isArray(msg.data?.movedItems) ? msg.data.movedItems : [];
        const campaignIdWs = msg.data?.campaignId as string | undefined;
        const campaignStatus = msg.data?.campaignStatus as Campaign['status'] | undefined;
        if (campaignIdWs && campaignStatus) {
          setCampaigns((prev) =>
            prev.map((c) => (c.campaignId === campaignIdWs ? { ...c, status: campaignStatus } : c))
          );
        }
        if (updates.length > 0) {
          const updatesById = new Map(
            updates.map((u: any) => {
              const patch: Partial<CampaignItem> & {
                pipelineStage: CampaignItemPipelineStage;
              } = {
                pipelineStage: u.pipelineStage as CampaignItemPipelineStage,
                repliedAt: u.repliedAt as string | undefined,
                updatedAt: u.updatedAt as string | undefined,
              };
              if (u.interessadoLigarEmMeses != null) {
                patch.interessadoLigarEmMeses = u.interessadoLigarEmMeses as number;
              }
              return [u.campaignItemId, patch] as const;
            })
          );
          setCampaignItems((prev) =>
            prev.map((item) =>
              updatesById.has(item.campaignItemId)
                ? { ...item, ...updatesById.get(item.campaignItemId)! }
                : item
            )
          );
        }
        // Sem payload de items: ignora. O `disparo_executed` (handler acima)
        // já cuida do contador. Fallback antigo recarregava ALL campaign-items
        // da org em cada evento — storm proporcional ao volume de disparo.
      }
    };
    wsService.on('disparo_executed', handleDisparoExecuted);
    wsService.on('campaign_item_updated', handleCampaignItemUpdated);
    return () => {
      wsService.off('disparo_executed', handleDisparoExecuted);
      wsService.off('campaign_item_updated', handleCampaignItemUpdated);
    };
  }, [loadCampaignData]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const resolveDropTarget = (overId: string, overData: any): DropTarget | null => {
    const parseColumnId = (columnId: string): DropTarget | null => {
      if (columnId.startsWith('posvenda:')) {
        const posVendaStatus = columnId.replace('posvenda:', '') as PosVendaStatus;
        if (resolvedPosVendaColumns.some((column) => column.id === posVendaStatus)) {
          return { type: 'pos_vendas', posVendaStatus };
        }
        return null;
      }

      const validStatuses: Lead['status'][] = [
        'aguardando_contato',
        'em_atendimento',
        'em_negociacao',
        'urgente',
        'proposta',
        'fechamento',
        'excluido',
      ];
      if (validStatuses.includes(columnId as Lead['status'])) {
        return { type: 'vendas', status: columnId as Lead['status'] };
      }
      return null;
    };

    // Drop over known droppable metadata (column/user/lead card)
    if (overData?.type === 'user-section' && overData.status) {
      const parsedTarget = parseColumnId(overData.status as string);
      if (parsedTarget) {
        return { ...parsedTarget, atendenteId: overData.atendenteId } as DropTarget;
      }
    }

    if (overData?.type === 'status-column' && overData.status) {
      return parseColumnId(overData.status as string);
    }

    if (overData?.type === 'lead') {
      if (overData.posVendaStatus) {
        return {
          type: 'pos_vendas',
          posVendaStatus: overData.posVendaStatus as PosVendaStatus,
          atendenteId: overData.atendenteId,
        };
      }
      if (overData.status) {
        return {
          type: 'vendas',
          status: overData.status as Lead['status'],
          atendenteId: overData.atendenteId,
        };
      }
    }

    // Fallback: plain column ids
    return parseColumnId(overId);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const leadId = active.id as string;
    const overId = over.id as string;
    const lead = leads.find((l) => l.leadId === leadId);
    const target = resolveDropTarget(overId, over.data?.current);

    if (!lead || !target) return;

    try {
      if (target.atendenteId && lead.atendenteId !== target.atendenteId && updateLeadAtendente) {
        await updateLeadAtendente(leadId, target.atendenteId);
      }

      if (target.type === 'vendas') {
        if (lead.status !== target.status) {
          await updateLeadStatus(leadId, target.status);
        }
        return;
      }

      // Pos-venda pipeline only accepts sold leads.
      if (lead.status !== 'fechamento' || lead.statusFechamento !== 'vendido') {
        window.alert('Apenas leads ganhos podem entrar no Kanban de pós-vendas.');
        return;
      }

      const currentPosVendaStatus = (lead.posVendaStatus || 'conversoes_ganhas') as PosVendaStatus;
      if (currentPosVendaStatus !== target.posVendaStatus) {
        await updateLeadStatus(leadId, 'fechamento', 'vendido', target.posVendaStatus);
      }
    } catch (error: any) {
      console.error('Erro ao atualizar lead no drag-and-drop:', error);
      const message = error?.response?.data?.message || 'Não foi possível transferir o lead.';
      window.alert(message);
    }
  };

  const handleStartLeadIa = React.useCallback(
    async (id: string) => {
      setIaOrchestrationPendingLeadId(id);
      try {
        await startLeadIa(id);
      } catch (e: any) {
        window.alert(getErrorMessage(e) || 'Não foi possível iniciar o atendimento por IA.');
      } finally {
        setIaOrchestrationPendingLeadId((cur) => (cur === id ? null : cur));
      }
    },
    [startLeadIa]
  );

  const handleAssumeHumanLeadIa = React.useCallback(
    async (id: string) => {
      try {
        await assumeHumanLeadIa(id);
      } catch (e: any) {
        window.alert(getErrorMessage(e) || 'Não foi possível assumir o atendimento.');
      }
    },
    [assumeHumanLeadIa]
  );

  const handleStopLeadIa = React.useCallback(
    async (id: string) => {
      try {
        await stopLeadIa(id);
      } catch (e: any) {
        window.alert(getErrorMessage(e) || 'Não foi possível parar a IA.');
      }
    },
    [stopLeadIa]
  );

  const atendenteNameMap = React.useMemo(() => {
    const map = new Map<string, string>();
    atendentes.forEach((u) => map.set(u.usuarioId, u.nome));
    return map;
  }, [atendentes]);

  const enrichLead = React.useCallback((lead: Lead): Lead => {
    if (lead.atendenteNome || !lead.atendenteId) return lead;
    const nome = atendenteNameMap.get(lead.atendenteId);
    return nome ? { ...lead, atendenteNome: nome } : lead;
  }, [atendenteNameMap]);

  const getLeadsByStatus = (status: Lead['status']) => {
    const normalizedNome = filters.nome.trim().toLowerCase();
    const normalizedCpf = filters.cpf.replace(/\D/g, '');
    const normalizedTelefone = filters.telefone.replace(/\D/g, '');

    return leads.filter((lead) => {
      // Exclude deleted leads
      if (lead.status === 'excluido') return false;
      if (lead.status !== status) return false;
      // Exclude leads that are already converted (vendido) or lost (nao_vendido)
      if (
        lead.status === 'fechamento' &&
        (lead.statusFechamento === 'vendido' ||
          lead.statusFechamento === 'nao_vendido' ||
          lead.statusFechamento === 'inativo')
      ) {
        return false;
      }
      if (normalizedNome && !lead.nome.toLowerCase().includes(normalizedNome)) {
        return false;
      }
      if (normalizedCpf) {
        const leadCpf = (lead.cpf || '').replace(/\D/g, '');
        if (!leadCpf.includes(normalizedCpf)) {
          return false;
        }
      }
      if (normalizedTelefone) {
        const leadTelefone = (lead.telefone || '').replace(/\D/g, '');
        if (!leadTelefone.includes(normalizedTelefone)) {
          return false;
        }
      }
      if (canFilterByAtendente && filters.atendenteId && lead.atendenteId !== filters.atendenteId) {
        return false;
      }
      if (filters.somenteHandoffFunilIa && !lead.aiFunnelHandoffAt) {
        return false;
      }
      if (filters.somenteReativacaoIa && !lead.isReativacao) {
        return false;
      }
      return true;
    }).map(enrichLead);
  };

  const getPosVendaStatus = (lead: Lead): PosVendaStatus | null => {
    if (lead.status !== 'fechamento' || lead.statusFechamento !== 'vendido') {
      return null;
    }
    return (lead.posVendaStatus || 'conversoes_ganhas') as PosVendaStatus;
  };

  const isLeadInsidePeriod = (dateValue: string | undefined, period: FinalizadoPeriod) => {
    if (!dateValue) return false;

    const leadDate = new Date(dateValue);
    if (Number.isNaN(leadDate.getTime())) return false;

    const now = new Date();
    const start = new Date(now);
    start.setHours(0, 0, 0, 0);

    if (period === 'today') {
      return leadDate >= start && leadDate <= now;
    }

    if (period === 'week') {
      start.setDate(now.getDate() - 6);
      return leadDate >= start && leadDate <= now;
    }

    if (period === 'month') {
      start.setDate(1);
      return leadDate >= start && leadDate <= now;
    }

    start.setMonth(0, 1);
    return leadDate >= start && leadDate <= now;
  };

  const matchesLeadFilters = (lead: Lead) => {
    const normalizedNome = filters.nome.trim().toLowerCase();
    const normalizedCpf = filters.cpf.replace(/\D/g, '');
    const normalizedTelefone = filters.telefone.replace(/\D/g, '');

    if (normalizedNome && !lead.nome.toLowerCase().includes(normalizedNome)) {
      return false;
    }
    if (normalizedCpf) {
      const leadCpf = (lead.cpf || '').replace(/\D/g, '');
      if (!leadCpf.includes(normalizedCpf)) {
        return false;
      }
    }
    if (normalizedTelefone) {
      const leadTelefone = (lead.telefone || '').replace(/\D/g, '');
      if (!leadTelefone.includes(normalizedTelefone)) {
        return false;
      }
    }
    if (canFilterByAtendente && filters.atendenteId && lead.atendenteId !== filters.atendenteId) {
      return false;
    }
    if (filters.somenteHandoffFunilIa && !lead.aiFunnelHandoffAt) {
      return false;
    }
    if (filters.somenteReativacaoIa && !lead.isReativacao) {
      return false;
    }
    return true;
  };

  const getLeadsByPosVendaStatus = (status: PosVendaStatus) => {
    return leads.filter((lead) => {
      if (getPosVendaStatus(lead) !== status) return false;
      if (!matchesLeadFilters(lead)) return false;
      if (status === 'finalizado' && !isLeadInsidePeriod(lead.updatedAt, finalizadoPeriod)) {
        return false;
      }
      return true;
    }).map(enrichLead);
  };

  const isLeadOverdue = (lead: Lead) => {
    const referenceDate = lead.ultimaMensagemAt || lead.updatedAt || lead.createdAt;
    const parsed = new Date(referenceDate || '');
    if (Number.isNaN(parsed.getTime())) return false;
    const oneDayMs = 24 * 60 * 60 * 1000;
    return Date.now() - parsed.getTime() > oneDayMs;
  };

  const handleConvert = async (leadId: string) => {
    try {
      await convertLead(leadId);
    } catch (error) {
      console.error('Erro ao converter lead:', error);
    }
  };

  const handleLose = async (leadId: string) => {
    try {
      await loseLead(leadId);
    } catch (error) {
      console.error('Erro ao marcar lead como perdido:', error);
    }
  };

  const handleOpenLead = (leadId: string) => {
    setSelectedLeadId(leadId);
    setActiveView('lead');
  };

  const handleCloseLeadDetails = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setSelectedLeadId(null);
    setActiveView(activeTab);
  };

  const activeLead = activeId ? leads.find((l) => l.leadId === activeId) : null;
  const selectedLead = selectedLeadId ? leads.find((lead) => lead.leadId === selectedLeadId) : null;

  // Determinar colunas a mostrar
  const columns = resolvedVendasColumns;
  const vendasLeads = columns.flatMap((column) => getLeadsByStatus(column.id));
  const posVendasLeads = resolvedPosVendaColumns.flatMap((column) => getLeadsByPosVendaStatus(column.id));

  const campaignById = React.useMemo(() => {
    const m = new Map<string, Campaign>();
    campaigns.forEach((c) => m.set(c.campaignId, c));
    return m;
  }, [campaigns]);

  const campaignItemsActiveCampaignsOnly = React.useMemo(
    () =>
      campaignItems.filter((i) => {
        const s = campaignById.get(i.campaignId)?.status;
        return s === 'ativa' || s === 'pendente';
      }),
    [campaignItems, campaignById]
  );

  const matchesCampaignFilters = React.useCallback(
    (item: CampaignItem) => {
      if (isCampaignsExecution) return true;
      const normalizedNome = filters.nome.trim().toLowerCase();
      const normalizedTelefone = filters.telefone.replace(/\D/g, '');
      const normalizedCampaignName = (campaignById.get(item.campaignId)?.nome || '').toLowerCase();
      const normalizedPersonName = (item.nome || '').toLowerCase();

      if (
        normalizedNome &&
        !normalizedPersonName.includes(normalizedNome) &&
        !normalizedCampaignName.includes(normalizedNome)
      ) {
        return false;
      }
      if (normalizedTelefone) {
        const itemPhone = (item.telefone || '').replace(/\D/g, '');
        if (!itemPhone.includes(normalizedTelefone)) return false;
      }
      return true;
    },
    [isCampaignsExecution, filters.nome, filters.telefone, campaignById]
  );

  /** Itens de campanhas ativas para pendente/disparado com filtro por pessoa/campanha. */
  const filteredActiveCampaignItems = React.useMemo(
    () => campaignItemsActiveCampaignsOnly.filter(matchesCampaignFilters),
    [campaignItemsActiveCampaignsOnly, matchesCampaignFilters]
  );

  /** Base terminal do Finalizado de campanhas (ativas + encerradas).
   * Items só vêm em memória quando o drawer/modal de uma campanha é aberto
   * (lazy fetch). Sem isso, a lista é vazia por design. */
  const terminalCampaignItemsByPeriod = React.useMemo(
    () =>
      campaignItems
        .filter((i) => CAMPAIGN_TERMINAL_STAGES.includes(i.pipelineStage))
        .filter(matchesCampaignFilters)
        .filter((i) => isLeadInsidePeriod(i.updatedAt, campaignFinalizadoPeriod)),
    [campaignItems, campaignFinalizadoPeriod, matchesCampaignFilters]
  );

  /** Campanhas que devem aparecer na coluna "Finalizado" (status=encerrada),
   * filtradas pelo período (updatedAt). Não depende de items pré-carregados. */
  const finalizadoCampaigns = React.useMemo(
    () =>
      campaigns
        .filter((c) => c.status === 'encerrada')
        .filter((c) =>
          isLeadInsidePeriod(c.updatedAt || c.createdAt, campaignFinalizadoPeriod)
        ),
    [campaigns, campaignFinalizadoPeriod]
  );

  /** Campanhas ativas (Em execução): independente de disparo ou items. */
  const activeCampaigns = React.useMemo(
    () => campaigns.filter((c) => c.status === 'ativa'),
    [campaigns]
  );

  /** Campanhas em pendente (Coluna Pendente): inicial ou retomada após Disparo terminal.
   *  Estado explícito de domínio — sem heurística de items/disparos. */
  const pendenteCampaigns = React.useMemo(
    () => campaigns.filter((c) => c.status === 'pendente'),
    [campaigns]
  );

  const campaignItemsByStage = React.useMemo(() => {
    const pendente = filteredActiveCampaignItems.filter((i) => i.pipelineStage === 'pendente');
    const disparado = filteredActiveCampaignItems.filter((i) => i.pipelineStage === 'disparado');
    const finalizado = terminalCampaignItemsByPeriod;
    return { pendente, disparado, finalizado };
  }, [filteredActiveCampaignItems, terminalCampaignItemsByPeriod]);

  /** Grupos do Finalizado: cards baseados em campanhas com status=encerrada
   * no período. Items terminais (se houver no estado por causa de drawer
   * aberto) servem como detalhe extra dentro do card. */
  const finalizadoGroups = React.useMemo(() => {
    const itemsByCampaign = new Map<string, CampaignItem[]>();
    for (const item of campaignItemsByStage.finalizado) {
      if (!itemsByCampaign.has(item.campaignId)) {
        itemsByCampaign.set(item.campaignId, []);
      }
      itemsByCampaign.get(item.campaignId)!.push(item);
    }
    const seen = new Set<string>();
    const groups: Array<{ campaignId: string; campaign: Campaign | undefined; items: CampaignItem[] }> = [];
    for (const c of finalizadoCampaigns) {
      seen.add(c.campaignId);
      groups.push({
        campaignId: c.campaignId,
        campaign: c,
        items: (itemsByCampaign.get(c.campaignId) || []).sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0).getTime() -
            new Date(a.updatedAt || a.createdAt || 0).getTime()
        ),
      });
    }
    // Campanhas ativas que têm items terminais no período mas a campanha em
    // si ainda não está encerrada (ex: alguns items chegaram a terminal mas
    // a campanha não foi formalmente encerrada). Mantém compatibilidade.
    itemsByCampaign.forEach((items, cid) => {
      if (seen.has(cid)) return;
      groups.push({
        campaignId: cid,
        campaign: campaignById.get(cid),
        items: items.sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt || 0).getTime() -
            new Date(a.updatedAt || a.createdAt || 0).getTime()
        ),
      });
    });
    return groups.sort((a, b) => {
      const aDate = a.campaign?.updatedAt || a.campaign?.createdAt || '';
      const bDate = b.campaign?.updatedAt || b.campaign?.createdAt || '';
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
  }, [campaignItemsByStage.finalizado, campaignById, finalizadoCampaigns]);

  const disparoCardInExecColumn = React.useCallback(
    (d: Disparo | undefined) => !!d && DISPARO_CARD_IN_EXEC_COLUMN.includes(d.status),
    []
  );

  /** Pendente: campanha ativa SEM disparo em estado ativo agora e SEM items
   * em stage='disparado'. Cobre os dois cenários:
   *   - campanha que nunca foi disparada
   *   - campanha que foi disparada antes mas o disparo concluiu (ou foi
   *     cancelado), e agora aguarda novo Play
   * Em ambos os casos o operador precisa de Play; coluna Em Execução não faz
   * sentido (não está executando).
   * Excluímos:
   *   - campanhas com items em stage 'disparado' (estão dispatching)
   *   - campanhas com disparo em estado ativo (em_execucao/pausado/etc)
   * Drafts (`status='rascunho'`) NÃO entram aqui — ficam só na lista de
   * Campanhas com botão Assistente pra editar. */
  const pendenteCampaignGroups = React.useMemo((): Array<[string, CampaignItem[]]> => {
    const m = new Map<string, CampaignItem[]>();
    for (const c of pendenteCampaigns) {
      m.set(c.campaignId, []);
    }
    for (const item of campaignItemsByStage.pendente) {
      const c = campaignById.get(item.campaignId);
      if (!c || c.status !== 'pendente') continue;
      const arr = m.get(item.campaignId);
      if (arr) arr.push(item);
    }
    return sortCampaignIdEntries(Array.from(m.entries()), campaignById);
  }, [pendenteCampaigns, campaignItemsByStage.pendente, campaignById]);

  /** Em execução: campanhas em `ativa`. Items em stage 'disparado' (lazy)
   * alimentam progresso; agora sem heurística cruzada — o backend (D8 do
   * change `add-campaign-pendente-state`) garante que campanhas aguardando
   * Play caem em `pendente` automaticamente. */
  const disparadoCampaignGroups = React.useMemo((): Array<[string, CampaignItem[]]> => {
    const m = new Map<string, CampaignItem[]>();
    for (const c of activeCampaigns) {
      m.set(c.campaignId, []);
    }
    for (const item of campaignItemsByStage.disparado) {
      const c = campaignById.get(item.campaignId);
      if (!c || c.status !== 'ativa') continue;
      const arr = m.get(item.campaignId);
      if (arr) arr.push(item);
    }
    return sortCampaignIdEntries(Array.from(m.entries()), campaignById);
  }, [campaignItemsByStage.disparado, campaignById, activeCampaigns]);

  // Contagem das colunas é # de grupos (= # de campanhas), não # de items.
  // Items são lazy desde a PR #77; somar items aqui daria 0 mesmo com cards
  // visíveis na coluna. Campaign-level alinha com o que o usuário vê.
  const campaignsExecPendenteItemCount = pendenteCampaignGroups.length;
  const campaignsExecDisparadoItemCount = disparadoCampaignGroups.length;

  const campaignItemsModalRows = React.useMemo(() => {
    if (!campaignItemsModalCampaignId) return [];
    return campaignItems.filter((i) => i.campaignId === campaignItemsModalCampaignId);
  }, [campaignItems, campaignItemsModalCampaignId]);

  // Lazy-load items pra campanha clicada (drawer ou modal). Items chegam
  // sob demanda e ficam em `campaignItems`; quem precisa (modal/drawer/cards)
  // filtra por campaignId. Concentra acesso a /campaign-items numa única
  // call/dropdown em vez de spamar tudo no mount.
  const fetchItemsForCampaign = React.useCallback(
    async (campaignId: string) => {
      try {
        const fresh = await apiService.listCampaignItems({ campaignId });
        setCampaignItems((prev) => {
          // Remove os items antigos dessa campaign e adiciona os recém-trazidos.
          const other = prev.filter((i) => i.campaignId !== campaignId);
          return [...other, ...fresh];
        });
      } catch (err) {
        console.error(`[KanbanBoard] listCampaignItems failed for ${campaignId}:`, err);
      }
    },
    []
  );

  useEffect(() => {
    if (executionDrawerCampaignId) {
      void fetchItemsForCampaign(executionDrawerCampaignId);
    }
  }, [executionDrawerCampaignId, fetchItemsForCampaign]);

  useEffect(() => {
    if (campaignItemsModalCampaignId) {
      void fetchItemsForCampaign(campaignItemsModalCampaignId);
    }
  }, [campaignItemsModalCampaignId, fetchItemsForCampaign]);

  const handleDisparoToggle = React.useCallback(async (disparoId: string, newStatus: 'em_execucao' | 'pausado') => {
    try {
      await apiService.updateDisparoStatus(disparoId, newStatus);
      await loadCampaignData();
    } catch (err: any) {
      console.error('Erro ao atualizar disparo:', err);
      const message = err?.response?.data?.message || 'Não foi possível alterar o disparo.';
      window.alert(message);
    }
  }, [loadCampaignData]);

  /** Primeiro Play: cria a fila com valores padrão e passa a em_execucao (o backend bloqueia se o número já estiver ocupado). */
  const handleQuickStartDisparo = React.useCallback(
    async (campaignId: string, whatsappAccountId: string) => {
      const wa = whatsappAccountId.trim();
      if (!wa) {
        window.alert('Defina a linha WhatsApp da campanha antes de iniciar o disparo.');
        return;
      }
      setQuickStartingCampaignId(campaignId);
      try {
        const campaign = campaignById.get(campaignId);
        const created = await apiService.createDisparo(
          buildCreateDisparoBodyFromCampaignDefaults(
            campaign ?? { campaignId },
            wa
          )
        );
        await apiService.updateDisparoStatus(created.disparoId, 'em_execucao');
        await loadCampaignData();
      } catch (err: any) {
        console.error('Erro ao criar/iniciar disparo:', err);
        const message = err?.response?.data?.message || 'Não foi possível criar ou iniciar o disparo.';
        window.alert(message);
      } finally {
        setQuickStartingCampaignId(null);
      }
    },
    [loadCampaignData, campaignById]
  );

  const handleFinalizeCampaign = React.useCallback(
    async (campaignId: string) => {
      const c = campaignById.get(campaignId);
      const message =
        c?.status === 'pendente'
          ? 'Encerrar esta campanha sem disparar? Os contactos configurados não serão enviados; a campanha sai do Kanban e vai pra Finalizado.'
          : 'Finalizar esta campanha? Os contactos ainda em «disparado» passam a «perdido» (remarketing). Quem já respondeu em «sucesso» não é alterado. Uma resposta depois pode voltar a marcar sucesso.';
      const ok = window.confirm(message);
      if (!ok) return;
      setFinalizingCampaignId(campaignId);
      try {
        const { campaign, movedItems } = await apiService.finalizeCampaign(campaignId);
        setCampaigns((prev) =>
          prev.map((c) => (c.campaignId === campaignId ? { ...c, ...campaign } : c))
        );
        const byId = new Map(movedItems.map((m) => [m.campaignItemId, m]));
        setCampaignItems((prev) =>
          prev.map((item) => {
            const m = byId.get(item.campaignItemId);
            return m
              ? {
                  ...item,
                  pipelineStage: m.pipelineStage,
                  updatedAt: m.updatedAt,
                  interessadoLigarEmMeses: m.interessadoLigarEmMeses,
                }
              : item;
          })
        );
        await loadCampaignData();
      } catch (err: any) {
        console.error('Erro ao finalizar campanha:', err);
        const message = err?.response?.data?.message || 'Não foi possível finalizar a campanha.';
        window.alert(message);
      } finally {
        setFinalizingCampaignId(null);
      }
    },
    [loadCampaignData, campaignById]
  );

  const moveCampaignItemToStage = async (campaignItemId: string, stage: CampaignItemPipelineStage) => {
    try {
      const updated = await apiService.updateCampaignItem(campaignItemId, { pipelineStage: stage });
      setCampaignItems((prev) => prev.map((x) => (x.campaignItemId === campaignItemId ? updated : x)));
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Nao foi possivel mover o item.';
      window.alert(message);
    } finally {
      setDraggingCampaignItemId(null);
    }
  };

  const resolveWhatsappLabel = React.useCallback(
    (camp: Campaign | undefined, disparo?: Disparo | undefined) => {
      const wid = (disparo?.whatsappAccountId || camp?.whatsappAccountId || '').trim();
      if (!wid) return '—';
      const acc = waAccounts.find((a) => a.accountId === wid);
      if (!acc) return wid;
      return formatWhatsappSelectLabel(acc, usuariosOrg);
    },
    [waAccounts, usuariosOrg]
  );

  const resolveResponsavel = React.useCallback(
    (camp: Campaign | undefined, disparo: Disparo | undefined) => {
      const dn = disparo?.createdByNome?.trim();
      if (dn) return dn;
      const id = camp?.createdByUsuarioId;
      if (id) {
        const u = usuariosOrg.find((x) => x.usuarioId === id);
        if (u?.nome?.trim()) return u.nome.trim();
      }
      return '—';
    },
    [usuariosOrg]
  );

  const renderDisparoControlsForCampaign = React.useCallback(
    (cId: string) => {
      const d = disparosByCampaign.get(cId);
      const camp = campaignById.get(cId);
      const allCampItems = campaignItemsActiveCampaignsOnly.filter((i) => i.campaignId === cId);
      const cPendentes = allCampItems.filter((i) => i.pipelineStage === 'pendente').length;
      const cDisparados = allCampItems.filter((i) => i.pipelineStage === 'disparado').length;
      const itemsLoaded = allCampItems.length > 0;
      // Sem items carregados (lazy desde PR #77), usamos `campaignItemCount`
      // e `disparosRealizados` agregado pra inferir:
      //   - dispatched: quantos já foram enviados
      //   - remaining:  quantos faltam disparar
      // Garante Play correto (só quando sobra fila) e Finalizar (só quando
      // tudo enviado), sem flicker entre os dois estados.
      const totalKnown = camp?.campaignItemCount ?? 0;
      const realizadosSum = realizadosByCampaign.get(cId) ?? 0;
      const dispatched = itemsLoaded ? cDisparados : realizadosSum;
      const remaining = itemsLoaded
        ? cPendentes
        : Math.max(0, totalKnown - realizadosSum);
      const totalProgress = itemsLoaded
        ? cPendentes + cDisparados
        : totalKnown;
      const canPlay = d ? ['criado', 'pausado', 'max_atingido'].includes(d.status) : false;
      const waCamp = (camp?.whatsappAccountId || '').trim();
      const canQuickStart =
        !d &&
        (camp?.status === 'pendente' || camp?.status === 'ativa') &&
        remaining > 0 &&
        !!waCamp;
      const canPause = d?.status === 'em_execucao';
      const isRunning = d?.status === 'em_execucao';
      const isForaHorario = d?.status === 'pausado_horario';
      const isMaxAtingido = d?.status === 'max_atingido';
      const showFinalize =
        camp?.viewerCanManage === true &&
        camp?.status === 'ativa' &&
        dispatched > 0 &&
        remaining === 0;
      const showEncerrar = camp?.viewerCanManage === true && camp?.status === 'pendente';
      const isPendenteRetomada = camp?.status === 'pendente' && (realizadosByCampaign.get(cId) ?? 0) > 0;
      const quickStartTooltip = canQuickStart
        ? isPendenteRetomada
          ? 'Retomar disparo com as preferências gravadas da campanha.'
          : 'Iniciar disparo agora com as preferências gravadas na campanha.'
        : '';
      const showRow =
        !!d || totalProgress > 0 || showFinalize || showEncerrar || isForaHorario || isMaxAtingido;
      if (!showRow) return null;

      return (
        <div className="campaign-disparo-controls">
          {isRunning && d ? <span className="campaign-disparo-pulse" /> : null}
          {isRunning && d ? <CountdownTimer targetIso={d.proximoDisparoEm} /> : null}
          {totalProgress > 0 ? (
            <span className="campaign-disparo-counter">
              {dispatched}/{totalProgress}
            </span>
          ) : null}
          {canPlay && d ? (
            <button
              type="button"
              className="campaign-footer-outline-btn campaign-footer-outline-btn--play"
              onClick={(e) => {
                e.stopPropagation();
                void handleDisparoToggle(d.disparoId, 'em_execucao');
              }}
            >
              <Play size={14} strokeWidth={2} aria-hidden />
              Play
            </button>
          ) : null}
          {canQuickStart ? (
            <button
              type="button"
              className="campaign-footer-outline-btn campaign-footer-outline-btn--play"
              disabled={quickStartingCampaignId === cId}
              title={quickStartTooltip}
              onClick={(e) => {
                e.stopPropagation();
                void handleQuickStartDisparo(cId, waCamp);
              }}
            >
              <Play size={14} strokeWidth={2} aria-hidden />
              {quickStartingCampaignId === cId ? '…' : 'Play'}
            </button>
          ) : null}
          {canPause && d ? (
            <button
              type="button"
              className="campaign-footer-outline-btn campaign-footer-outline-btn--pause"
              onClick={(e) => {
                e.stopPropagation();
                void handleDisparoToggle(d.disparoId, 'pausado');
              }}
            >
              <Pause size={14} strokeWidth={2} aria-hidden />
              Pause
            </button>
          ) : null}
          {showFinalize ? (
            <button
              type="button"
              className="campaign-footer-outline-btn campaign-footer-outline-btn--finalize"
              disabled={finalizingCampaignId === cId}
              title="Marcar como concluída — contactos em «disparado» viram «perdido» (remarketing)."
              onClick={(e) => {
                e.stopPropagation();
                void handleFinalizeCampaign(cId);
              }}
            >
              <CircleCheck size={14} strokeWidth={2} aria-hidden />
              {finalizingCampaignId === cId ? '…' : 'Finalizar'}
            </button>
          ) : null}
          {showEncerrar ? (
            <button
              type="button"
              className="campaign-footer-outline-btn campaign-footer-outline-btn--finalize"
              disabled={finalizingCampaignId === cId}
              title="Encerrar sem disparar items restantes."
              onClick={(e) => {
                e.stopPropagation();
                void handleFinalizeCampaign(cId);
              }}
            >
              <CircleCheck size={14} strokeWidth={2} aria-hidden />
              {finalizingCampaignId === cId ? '…' : 'Encerrar'}
            </button>
          ) : null}
          {isForaHorario ? (
            <span
              className="campaign-disparo-badge campaign-disparo-badge--horario"
              title="Disparo pausado automaticamente por estar fora da janela de horário configurada"
            >
              Fora de horário
            </span>
          ) : null}
          {isMaxAtingido ? (
            <span
              className="campaign-disparo-badge campaign-disparo-badge--max"
              title={`Limite de ${d?.maxDisparosPorNumero ?? ''} disparos atingido. Play inicia uma nova rodada de ${d?.maxDisparosPorNumero ?? ''} envios. Para disparar até o fim sem parar, ative “Disparar até o fim” em Histórico de Disparos → Horário.`}
            >
              Máx. atingido
            </span>
          ) : null}
        </div>
      );
    },
    [
      disparosByCampaign,
      campaignItemsActiveCampaignsOnly,
      campaignById,
      handleDisparoToggle,
      handleQuickStartDisparo,
      handleFinalizeCampaign,
      finalizingCampaignId,
      quickStartingCampaignId,
    ]
  );

  const summarizePipeline = (pipelineLeads: Lead[]): PipelineSummary => ({
    leads: pipelineLeads,
    unreadCount: pipelineLeads.reduce((total, lead) => total + (lead.unreadCount || 0), 0),
    overdueCount: pipelineLeads.reduce((total, lead) => total + (isLeadOverdue(lead) ? 1 : 0), 0),
  });

  const vendasSummary = summarizePipeline(vendasLeads);
  const posVendasSummary = summarizePipeline(posVendasLeads);
  /** Disparos em estado que ocupam a fila do número (uma visão de “campanha ativa”). */
  const activeCampanhaDisparoCount = React.useMemo(() => {
    const active = new Set<Disparo['status']>([
      'criado',
      'em_execucao',
      'pausado',
      'pausado_horario',
      'max_atingido',
    ]);
    return Array.from(disparosByCampaign.values()).filter((d) => active.has(d.status)).length;
  }, [disparosByCampaign]);
  const activeKanbanName =
    activeView === 'lead'
      ? `Card de ${selectedLead?.nome || 'Lead'}`
      : activeTab === 'vendas'
      ? 'Kanban de Vendas'
      : activeTab === 'pos_vendas'
      ? 'Kanban de Pos-venda'
      : 'Kanban de Campanhas';
  // No tab "campanhas", o total é # de cards (grupos) das 3 colunas. Items
  // não são mais a base por causa do lazy load (PR #77).
  const activeKanbanLeadCount =
    activeTab === 'vendas'
      ? vendasSummary.leads.length
      : activeTab === 'pos_vendas'
      ? posVendasSummary.leads.length
      : pendenteCampaignGroups.length + disparadoCampaignGroups.length + finalizadoGroups.length;
  const activeKanbanLeadLabel =
    activeTab === 'campanhas'
      ? `${activeKanbanLeadCount} ${activeKanbanLeadCount === 1 ? 'campanha' : 'campanhas'}`
      : `${activeKanbanLeadCount} ${activeKanbanLeadCount === 1 ? 'lead' : 'leads'}`;

  if (!isCampaignsExecution && loading && activeTab !== 'campanhas') {
    return (
      <div className="kanban-loading">
        <div className="spinner"></div>
        <p>Carregando leads...</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={`kanban-board-layout${isCampaignsExecution ? ' kanban-board-layout--campaigns-only' : ''}`}>
        {!isCampaignsExecution && (
          <KanbanFilters
            value={filters}
            users={atendentes}
            showAtendenteFilter={canFilterByAtendente}
            onChange={setFilters}
            onCreateLead={onCreateLead}
            isCreateLeadExpanded={isCreateLeadExpanded}
          />
        )}
        {!isCampaignsExecution && createLeadPanel}
        {!isCampaignsExecution && createDisparoPanel}
        {!isCampaignsExecution && (
        <div className="kanban-tabs">
          <button
            type="button"
            className={`kanban-tab tab-vendas ${activeView === 'vendas' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('vendas');
              setActiveView('vendas');
              setFinalizadoPeriod('today');
            }}
          >
            <span className="kanban-tab-title">Vendas</span>
            <span className="kanban-tab-meta">
              {vendasSummary.leads.length} leads
            </span>
            <span className="kanban-tab-unread-badge">
              {vendasSummary.unreadCount > 99 ? '99+' : vendasSummary.unreadCount}
            </span>
          </button>
          <button
            type="button"
            className={`kanban-tab tab-pos-vendas ${activeView === 'pos_vendas' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('pos_vendas');
              setActiveView('pos_vendas');
              setFinalizadoPeriod('today');
            }}
          >
            <span className="kanban-tab-title">Pós-vendas</span>
            <span className="kanban-tab-meta">
              {posVendasSummary.leads.length} leads
            </span>
            <span className="kanban-tab-unread-badge">
              {posVendasSummary.unreadCount > 99 ? '99+' : posVendasSummary.unreadCount}
            </span>
          </button>
          {CAMPAIGNS_ENABLED && (
            <button
              type="button"
              className={`kanban-tab tab-campanhas ${activeView === 'campanhas' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('campanhas');
                setActiveView('campanhas');
              }}
            >
              <span className="kanban-tab-title">Campanhas</span>
              <span className="kanban-tab-meta">
                {activeCampanhaDisparoCount === 0
                  ? '0 ativas'
                  : `${activeCampanhaDisparoCount} ${
                      activeCampanhaDisparoCount === 1 ? 'ativa' : 'ativas'
                    }`}
              </span>
            </button>
          )}
          {selectedLead && (
            <div
              role="button"
              tabIndex={0}
              className={`kanban-tab tab-lead-details ${activeView === 'lead' ? 'active' : ''}`}
              onClick={() => setActiveView('lead')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setActiveView('lead');
                }
              }}
            >
              <span className="kanban-tab-title">{selectedLead.nome}</span>
              <span className="kanban-tab-meta">Card aberto</span>
              <span className="lead-tab-unread-badge">
                {(selectedLead.unreadCount || 0) > 99 ? '99+' : (selectedLead.unreadCount || 0)}
              </span>
              <button
                type="button"
                aria-label="Fechar detalhe do lead"
                className="lead-tab-close-btn"
                onClick={handleCloseLeadDetails}
              >
                ×
              </button>
            </div>
          )}
        </div>
        )}
        <div className="kanban-active-wrapper">
          <div
            className="kanban-active-header"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
          >
            <h3 style={{ margin: 0 }}>
              {activeView === 'lead' ? activeKanbanName : `${activeKanbanName} (${activeKanbanLeadLabel})`}
            </h3>
            {activeView !== 'lead' && activeTab !== 'campanhas' && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <AutorunAdminMenu />
                <KanbanIaToggle />
              </div>
            )}
          </div>
          {activeView === 'lead' && selectedLead ? (
            <div className="kanban-lead-details-container">
              <LeadDetails
                leadId={selectedLead.leadId}
                embedded
                onBackToDashboard={() => {
                  setActiveView(activeTab);
                }}
              />
            </div>
          ) : (
            <>
            <div className="kanban-board-scroll">
              <div className="kanban-board">
                {activeTab === 'vendas' &&
                columns.map((column) => {
                  const columnLeads = getLeadsByStatus(column.id);

                  return (
                    <SortableContext
                      key={column.id}
                      items={columnLeads.map((l) => l.leadId)}
                      strategy={verticalListSortingStrategy}
                    >
                      <KanbanColumn
                        id={column.id}
                        title={column.title}
                        leads={columnLeads}
                        onConvert={handleConvert}
                        onLose={handleLose}
                        onOpenLead={handleOpenLead}
                        onStartLeadIa={handleStartLeadIa}
                        onAssumeHumanLeadIa={handleAssumeHumanLeadIa}
                        onStopLeadIa={handleStopLeadIa}
                        aiOrchestrationEnabled={orgAiEnabled}
                        iaOrchestrationPendingLeadId={iaOrchestrationPendingLeadId}
                      />
                    </SortableContext>
                  );
                })}

                {activeTab === 'pos_vendas' &&
                resolvedPosVendaColumns.map((column) => {
                  const columnLeads = getLeadsByPosVendaStatus(column.id);
                  const dropId = `posvenda:${column.id}`;
                  const isFinalizado = column.id === 'finalizado';
                  const headerExtra = isFinalizado ? (
                    <div className="won-column-filters">
                      {(Object.keys(FINALIZADO_PERIOD_LABELS) as FinalizadoPeriod[]).map((period) => (
                        <button
                          key={period}
                          type="button"
                          className={`won-filter-btn ${finalizadoPeriod === period ? 'active' : ''}`}
                          onClick={() => setFinalizadoPeriod(period)}
                        >
                          {FINALIZADO_PERIOD_LABELS[period]}
                        </button>
                      ))}
                    </div>
                  ) : null;

                  return (
                    <SortableContext
                      key={dropId}
                      items={columnLeads.map((l) => l.leadId)}
                      strategy={verticalListSortingStrategy}
                    >
                      <KanbanColumn
                        id={dropId}
                        title={column.title}
                        leads={columnLeads}
                        headerExtra={headerExtra}
                        columnClassName="pos-venda-column"
                        onOpenLead={handleOpenLead}
                      />
                    </SortableContext>
                  );
                })}

                {activeTab === 'campanhas' && (
                  <>
                    {isCampaignsExecution && (
                      <>
                      <div key="campaign-exec-pendente" className="kanban-column lost-kanban-column">
                          <div className="kanban-column-header">
                            <h3>{resolvedCampanhasLabels.pendente}</h3>
                            <span className="lead-count">{campaignsExecPendenteItemCount}</span>
                          </div>
                          <div className="kanban-column-content">
                            {campaignLoading ? (
                              <div className="empty-column">Carregando itens...</div>
                            ) : pendenteCampaignGroups.length === 0 ? (
                              <div className="empty-column">Nenhum item nesta coluna</div>
                            ) : (
                              pendenteCampaignGroups.map(([cId, items]) => {
                                const camp = campaignById.get(cId);
                                const d = disparosByCampaign.get(cId);
                                return (
                                  <div key={cId} className="campaign-group campaign-group--aggregate-only">
                                    <CampaignExecutionCard
                                      campaignId={cId}
                                      camp={camp}
                                      columnItems={items}
                                      allActiveCampaignItems={campaignItemsActiveCampaignsOnly.filter(
                                        (i) => i.campaignId === cId
                                      )}
                                      columnKind="pendente"
                                      disparo={d}
                                      contaLabel={resolveWhatsappLabel(camp, d)}
                                      responsavelLabel={resolveResponsavel(camp, d)}
                                      onOpenListModal={() => {
                                        setExecutionDrawerCampaignId(null);
                                        setCampaignItemsModalCampaignId(cId);
                                      }}
                                      onOpenDetailDrawer={() => {
                                        setCampaignItemsModalCampaignId(null);
                                        setExecutionDrawerCampaignId(cId);
                                      }}
                                      disparoControls={renderDisparoControlsForCampaign(cId)}
                                    />
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                        <div key="campaign-exec-disparado" className="kanban-column lost-kanban-column">
                          <div className="kanban-column-header">
                            <h3>{resolvedCampanhasLabels.emExecucao}</h3>
                            <span className="lead-count">{campaignsExecDisparadoItemCount}</span>
                          </div>
                          <div className="kanban-column-content">
                            {campaignLoading ? (
                              <div className="empty-column">Carregando itens...</div>
                            ) : disparadoCampaignGroups.length === 0 ? (
                              <div className="empty-column">Nenhum item nesta coluna</div>
                            ) : (
                              disparadoCampaignGroups.map(([cId, items]) => {
                                const camp = campaignById.get(cId);
                                const d = disparosByCampaign.get(cId);
                                return (
                                  <div key={cId} className="campaign-group campaign-group--aggregate-only">
                                    <CampaignExecutionCard
                                      campaignId={cId}
                                      camp={camp}
                                      columnItems={items}
                                      allActiveCampaignItems={campaignItemsActiveCampaignsOnly.filter(
                                        (i) => i.campaignId === cId
                                      )}
                                      columnKind="disparado"
                                      disparo={d}
                                      contaLabel={resolveWhatsappLabel(camp, d)}
                                      responsavelLabel={resolveResponsavel(camp, d)}
                                      onOpenListModal={() => {
                                        setExecutionDrawerCampaignId(null);
                                        setCampaignItemsModalCampaignId(cId);
                                      }}
                                      onOpenDetailDrawer={() => {
                                        setCampaignItemsModalCampaignId(null);
                                        setExecutionDrawerCampaignId(cId);
                                      }}
                                      disparoControls={renderDisparoControlsForCampaign(cId)}
                                    />
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </>
                    )}
                    {!isCampaignsExecution &&
                      resolvedCampaignActiveColumns.map((column) => {
                      const isPendente = column.id === 'pendente';
                      const entries = isPendente ? pendenteCampaignGroups : disparadoCampaignGroups;
                      const columnItemCount = entries.reduce((sum, [, its]) => sum + its.length, 0);
                      return (
                        <div
                          key={column.id}
                          className="kanban-column lost-kanban-column"
                          onDragOver={(event) => event.preventDefault()}
                          onDrop={() => {
                            if (draggingCampaignItemId) {
                              void moveCampaignItemToStage(draggingCampaignItemId, column.id);
                            }
                          }}
                        >
                          <div className="kanban-column-header">
                            <h3>{column.title}</h3>
                            <span className="lead-count">{columnItemCount}</span>
                          </div>
                          <div className="kanban-column-content">
                            {campaignLoading ? (
                              <div className="empty-column">Carregando itens...</div>
                            ) : entries.length === 0 ? (
                              <div className="empty-column">Nenhum item nesta coluna</div>
                            ) : (
                              entries.map(([cId, items]) => {
                                const camp = campaignById.get(cId);
                                const d = disparosByCampaign.get(cId);
                                return (
                                  <div key={cId} className="campaign-group campaign-group--aggregate-only">
                                    <CampaignExecutionCard
                                      campaignId={cId}
                                      camp={camp}
                                      columnItems={items}
                                      allActiveCampaignItems={campaignItemsActiveCampaignsOnly.filter(
                                        (i) => i.campaignId === cId
                                      )}
                                      columnKind={isPendente ? 'pendente' : 'disparado'}
                                      disparo={d}
                                      contaLabel={resolveWhatsappLabel(camp, d)}
                                      responsavelLabel={resolveResponsavel(camp, d)}
                                      onOpenListModal={() => {
                                        setExecutionDrawerCampaignId(null);
                                        setCampaignItemsModalCampaignId(cId);
                                      }}
                                      onOpenDetailDrawer={() => {
                                        setCampaignItemsModalCampaignId(null);
                                        setExecutionDrawerCampaignId(cId);
                                      }}
                                      disparoControls={renderDisparoControlsForCampaign(cId)}
                                    />
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {(() => {
                      // Contagem da coluna: # de grupos (campanhas encerradas no período).
                      // O fallback antigo usava só items terminais carregados, que ficavam
                      // zerados após o lazy load da PR #77.
                      return (
                        <div className="kanban-column lost-kanban-column campaign-finalizado-column">
                          <div className="kanban-column-header">
                            <h3>{resolvedCampanhasLabels.finalizado}</h3>
                            <span className="lead-count">{finalizadoGroups.length}</span>
                            <div className="won-column-filters">
                              {(Object.keys(FINALIZADO_PERIOD_LABELS) as FinalizadoPeriod[]).map((period) => (
                                <button
                                  key={period}
                                  type="button"
                                  className={`won-filter-btn ${campaignFinalizadoPeriod === period ? 'active' : ''}`}
                                  onClick={() => setCampaignFinalizadoPeriod(period)}
                                >
                                  {FINALIZADO_PERIOD_LABELS[period]}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="kanban-column-content campaign-finalizado-content">
                            {campaignLoading ? (
                              <div className="empty-column">Carregando itens...</div>
                            ) : (
                              <div className="campaign-finalizado-cards">
                                {finalizadoGroups.length === 0 ? (
                                  <div className="empty-column subtle">Nenhum item</div>
                                ) : (
                                  finalizadoGroups.map((group) => {
                                    const d = disparosByCampaign.get(group.campaignId);
                                    const allForCamp = campaignItems.filter((i) => i.campaignId === group.campaignId);
                                    return (
                                      <div
                                        key={group.campaignId}
                                        className="campaign-group campaign-group--aggregate-only"
                                      >
                                        <CampaignExecutionCard
                                          campaignId={group.campaignId}
                                          camp={group.campaign}
                                          columnItems={group.items}
                                          allActiveCampaignItems={allForCamp}
                                          columnKind="finalizado"
                                          disparo={d}
                                          contaLabel={resolveWhatsappLabel(group.campaign, d)}
                                          responsavelLabel={resolveResponsavel(group.campaign, d)}
                                          onOpenListModal={() => {
                                            setExecutionDrawerCampaignId(null);
                                            setCampaignItemsModalCampaignId(group.campaignId);
                                          }}
                                          onOpenDetailDrawer={() => {
                                            setCampaignItemsModalCampaignId(null);
                                            setExecutionDrawerCampaignId(group.campaignId);
                                          }}
                                          disparoControls={
                                            <span className="campaign-kanban-aggregate-final-hint campaign-kanban-aggregate-footer-row campaign-kanban-aggregate-footer-row--start">
                                              {group.items.length}{' '}
                                              {group.items.length === 1 ? 'contato' : 'contatos'} em estágio final
                                              neste período
                                            </span>
                                          }
                                        />
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
            </>
          )}
        </div>
      </div>
      <DragOverlay>
        {activeLead ? (
          <LeadCard
            lead={activeLead}
            isDragging
            iaStartPending={iaOrchestrationPendingLeadId === activeLead.leadId}
          />
        ) : null}
      </DragOverlay>
      <CampaignItemsListModal
        open={!!campaignItemsModalCampaignId}
        campaign={
          campaignItemsModalCampaignId
            ? campaignById.get(campaignItemsModalCampaignId)
            : undefined
        }
        items={campaignItemsModalRows}
        onClose={() => setCampaignItemsModalCampaignId(null)}
      />
      <CampaignExecutionDrawer
        open={!!executionDrawerCampaignId}
        campaign={
          executionDrawerCampaignId ? campaignById.get(executionDrawerCampaignId) : undefined
        }
        items={
          executionDrawerCampaignId
            ? campaignItems.filter((i) => i.campaignId === executionDrawerCampaignId)
            : []
        }
        disparo={
          executionDrawerCampaignId ? disparosByCampaign.get(executionDrawerCampaignId) : undefined
        }
        contaWhatsappLabel={
          executionDrawerCampaignId
            ? resolveWhatsappLabel(
                campaignById.get(executionDrawerCampaignId),
                disparosByCampaign.get(executionDrawerCampaignId)
              )
            : '—'
        }
        responsavelLabel={
          executionDrawerCampaignId
            ? resolveResponsavel(
                campaignById.get(executionDrawerCampaignId),
                disparosByCampaign.get(executionDrawerCampaignId)
              )
            : '—'
        }
        defaultRemarketingMeses={orgDefaultRemarketingMeses}
        onClose={() => setExecutionDrawerCampaignId(null)}
      />
    </DndContext>
  );
};
